import { Controller } from "@hotwired/stimulus"
import { savePlan } from "plan_storage"

// Client City + State + ZIP picker — Rails-native port of
// renderClientCityInline + renderClientZipPicker in
// editor_source/pcp_builder.html#10580 and #10466. Lives alongside the
// plan-store controller so the city/state values round-trip through
// `data-plan-field` exactly like every other text input.
//
// The actual ZIP → place lookup table isn't ported yet; this controller
// renders the picker chips when plan.client._zipPlaces has data and
// hides them otherwise, matching the visual behavior the standard
// editor exposes.
const STORAGE_KEY = "pcp_plan_data"

export default class extends Controller {
  static targets = [ "zipPicker", "zipLabel", "zipChips" ]

  connect() {
    this._refreshZipPicker()
    // Re-render when the plan changes so the city/state fields + multi-city ZIP
    // picker stay in sync after a plan is loaded from .sdp, edited in the wizard
    // pane, or undone/redone — not just on direct typing. The correct event is
    // "plan:change" (plan-store dispatches that); the old "plan-store:change"
    // name never fired, so those paths silently didn't re-sync.
    this._onPlanChanged = () => this._refreshZipPicker()
    document.addEventListener("plan:change", this._onPlanChanged)
    // ZIP autofill. We listen to BOTH client.address (legacy: address
    // = ZIP in the old combined field) AND client.zip (current: the
    // dedicated ZIP input). On a 5-digit value we ping zippopotam.us
    // and write back client.city + client.state + client._zipPlaces.
    this._onAddressInput = (event) => {
      const el = event.target
      const field = el?.dataset?.planField
      if (field !== "client.address" && field !== "client.zip") return
      // For client.zip we route through the ZIP-specific path that
      // overrides whatever city/state the address autocomplete set.
      if (field === "client.zip") {
        this._handleZipInput(el.value || "")
      } else {
        this._handleAddressInput(el.value || "")
      }
    }
    document.addEventListener("input", this._onAddressInput, true)
    // Fire once on connect so pre-filled values still trigger the
    // lookup after a page refresh.
    const addr = document.querySelector("[data-plan-field='client.address']")
    if (addr?.value) this._handleAddressInput(addr.value)
    const zip = document.querySelector("[data-plan-field='client.zip']")
    if (zip?.value) this._handleZipInput(zip.value)
  }

  disconnect() {
    document.removeEventListener("plan:change", this._onPlanChanged)
    document.removeEventListener("input", this._onAddressInput, true)
  }

  // Dedicated ZIP-field handler. The address-autocomplete may have set
  // city/state from a Nominatim guess — but if the user then types a
  // specific ZIP, that ZIP is the source of truth. We unconditionally
  // call zippopotam.us and overwrite the city/state if the cached
  // value doesn't match a place served by that ZIP.
  _handleZipInput(value) {
    const zip = String(value || "").trim()
    const plan = this._readPlan()
    if (!plan.client) plan.client = {}
    if (!/^\d{5}$/.test(zip)) return
    plan.client.zip = zip
    this._writePlan(plan)
    this._lookupZip(zip, { force: true })
  }

  // ---- ZIP autofill ------------------------------------------------
  // Legacy: the old combined "Location" input was the ZIP itself, so
  // typing into client.address used to trigger a zippopotam lookup.
  // After the refactor, street address and ZIP are separate fields —
  // _handleAddressInput now only fires the ZIP lookup if the user
  // typed a bare 5-digit ZIP into the address field (someone using
  // the old habit). Anything else: leave city/state/zip alone so the
  // address-autocomplete and the dedicated ZIP listener stay in
  // charge.
  _handleAddressInput(value) {
    const v = String(value || "").trim()
    if (!/^\d{5}$/.test(v)) return
    const plan = this._readPlan()
    if (!plan.client) plan.client = {}
    plan.client.zip = v
    this._writePlan(plan)
    this._lookupZip(v, { force: true })
  }

  async _lookupZip(zip, { force = false } = {}) {
    // Cache lookups per session to avoid hammering the public API.
    this.constructor._zipCache = this.constructor._zipCache || {}
    let result = this.constructor._zipCache[zip]
    try {
      if (!result) {
        const res = await fetch("https://api.zippopotam.us/us/" + encodeURIComponent(zip))
        if (!res.ok) return
        const data = await res.json()
        const places = (data.places || [])
          .map(p => ({ city: p["place name"] || "", state: p["state abbreviation"] || "" }))
          .filter(p => p.city)
        result = { places }
        this.constructor._zipCache[zip] = result
      }
      if (!result.places || result.places.length === 0) return
      const plan = this._readPlan()
      if (!plan.client) plan.client = {}
      plan.client._zipPlaces = result.places
      plan.client._zip       = zip
      // Default behavior: keep the user's existing pick if it's one of
      // the cities this ZIP covers; otherwise default to the first.
      // force=true overrides any prior city/state — used when the ZIP
      // input changes directly (the user just told us a new ZIP).
      const currentCity = (plan.client.city || "").trim()
      const stillValid  = result.places.some(p => p.city === currentCity)
      if (force || !stillValid) {
        plan.client.city  = result.places[0].city
        plan.client.state = result.places[0].state || ""
      }
      this._writePlan(plan)
      this._reflectInputs(plan)
      this._refreshZipPicker()
      // Also push the resolved city/state to any plan-field inputs
      // anywhere on the page (the wizard pane may render its own
      // city/state inputs in addition to the editor pane).
      this._broadcastFields(plan)
    } catch (_) { /* network/CORS — silent fallback */ }
  }

  _broadcastFields(plan) {
    const city  = plan?.client?.city  || ""
    const state = plan?.client?.state || ""
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set
    document.querySelectorAll("[data-plan-field='client.city']").forEach(el => {
      if (el.value === city) return
      if (setter) setter.call(el, city); else el.value = city
      el.dispatchEvent(new Event("input",  { bubbles: true }))
      el.dispatchEvent(new Event("change", { bubbles: true }))
    })
    document.querySelectorAll("[data-plan-field='client.state']").forEach(el => {
      if (el.value === state) return
      if (setter) setter.call(el, state); else el.value = state
      el.dispatchEvent(new Event("input",  { bubbles: true }))
      el.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  // Push the persisted city/state back into the visible inputs without a
  // full re-render. Dispatches a native input event so plan-store + any
  // other listeners pick up the change.
  _reflectInputs(plan) {
    const cityInput  = this.element.querySelector("[data-plan-field='client.city']")
    const stateInput = this.element.querySelector("[data-plan-field='client.state']")
    if (cityInput && cityInput.value !== (plan.client.city || "")) {
      cityInput.value = plan.client.city || ""
    }
    if (stateInput && stateInput.value !== (plan.client.state || "")) {
      stateInput.value = plan.client.state || ""
    }
  }

  // State field — production force-uppercases as the user types.
  upcaseState(event) {
    const el = event.target
    const start = el.selectionStart
    el.value = (el.value || "").toUpperCase()
    if (start != null) el.setSelectionRange(start, start)
  }

  // User picks one of the cities the ZIP maps to. Updates plan.client
  // directly and reflects it back into both inputs.
  pickCity(event) {
    const idx = parseInt(event.currentTarget.dataset.cityIdx, 10)
    if (!Number.isFinite(idx)) return
    const plan = this._readPlan()
    const place = plan.client?._zipPlaces?.[idx]
    if (!place) return
    plan.client.city  = place.city  || ""
    plan.client.state = place.state || ""
    this._writePlan(plan)
    // Mirror into the visible inputs without a full re-render.
    const cityInput  = this.element.querySelector("[data-plan-field='client.city']")
    const stateInput = this.element.querySelector("[data-plan-field='client.state']")
    if (cityInput)  cityInput.value  = plan.client.city
    if (stateInput) stateInput.value = plan.client.state
    this._refreshZipPicker()
  }

  // ---------------------------------------------------------------
  _refreshZipPicker() {
    if (!this.hasZipPickerTarget) return
    const plan = this._readPlan()
    const places = (plan?.client?._zipPlaces) || []
    if (places.length <= 1) {
      this.zipPickerTarget.hidden = true
      return
    }
    const currentCity = (plan?.client?.city || "").trim()
    this.zipPickerTarget.hidden = false
    if (this.hasZipLabelTarget) {
      this.zipLabelTarget.textContent = `This ZIP covers ${places.length} cities — pick:`
    }
    if (this.hasZipChipsTarget) {
      this.zipChipsTarget.innerHTML = places.map((p, i) => {
        const selected = p.city === currentCity
        const cls = selected
          ? "bg-blue-600 border-blue-600 text-white shadow-sm"
          : "bg-white border-blue-300 text-blue-800 hover:bg-blue-100"
        const label = p.city + (p.state ? ", " + p.state : "")
        return `<button type="button"
          data-action="click->client-city#pickCity"
          data-city-idx="${i}"
          class="px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors ${cls}">${escapeHtml(label)}</button>`
      }).join("")
    }
  }

  _readPlan() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {} }
    catch (_) { return {} }
  }
  _writePlan(plan) {
    savePlan(plan)
  }
}

function escapeHtml(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
}
